import { SchemaItem, SchemaItemCategory, StructureItemType, StructureColumnTypeEnum } from "./Models/Models";
import Papa from 'papaparse';

export enum FileType {
    Csv = 'csv',
    Json = 'json'
}

function detectFileType(content: string): FileType {
    const trimmed = content.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        return FileType.Json;
    }
    return FileType.Csv;
}

export type SchemaValue = boolean | number | string | number[];

export type SchemaDataRow = SchemaValue[];

export interface ParseResult {
    data: SchemaDataRow[];
    schemaOrdering: string[];
    isValid: boolean;
    error?: string;
}

function parseValue(value: string, schemaItem: SchemaItem, lineNumber: number): SchemaValue {
    const trimmedValue = value.trim();

    switch (schemaItem.valueTypeId) {
        case StructureItemType.Category:
            if (!schemaItem.categories || schemaItem.categories.length === 0) {
                throw new Error(`Line ${lineNumber}: No categories defined for ${schemaItem.name}`);
            }
            const validCategories = schemaItem.categories.map(c => c.value.toLocaleLowerCase());
            if (!validCategories.includes(trimmedValue.toLocaleLowerCase())) {
                throw new Error(`Line ${lineNumber}: Invalid category value for ${schemaItem.name}: "${trimmedValue}". Valid values are: ${validCategories.join(", ")}`);
            }
            return trimmedValue;
        case StructureItemType.Number:
            const numberValue = parseFloat(trimmedValue);
            if (isNaN(numberValue)) {
                throw new Error(`Line ${lineNumber}: Invalid number for ${schemaItem.name}: "${trimmedValue}"`);
            }
            return numberValue;
        case StructureItemType.String:
            return trimmedValue;
        case StructureItemType.NumberArray:
            throw new Error(`Line ${lineNumber}: Array values should be handled separately`);
        default:
            return trimmedValue;
    }
}


export function parseCsvData(csvString: string, schemaItems: SchemaItem[]): ParseResult {
    try {
        const parseResult = Papa.parse<string[]>(csvString, {
            skipEmptyLines: true,
        });

        if (parseResult.errors && parseResult.errors.length > 0) {
            const firstError = parseResult.errors[0];
            return {
                data: [],
                schemaOrdering: [],
                isValid: false,
                error: `CSV parse error at row ${firstError.row}: ${firstError.message}`
            };
        }

        const rows = parseResult.data;

        if (!rows || rows.length < 2) {
            return {
                data: [],
                schemaOrdering: [],
                isValid: false,
                error: "CSV must have at least a header row and one data row"
            };
        }

        const header = rows[0].map((h: string) => h.trim());
        const dataRows = rows.slice(1);

        const schemaOrderingSet = new Set<string>();
        header.forEach(h => {
            const match = h.match(/^(.+)_\d+$/);
            if (match) {
                schemaOrderingSet.add(match[1]);
            } else {
                schemaOrderingSet.add(h);
            }
        });

        const expectedHeaders = new Set<string>();
        const optionalHeaders = new Set<string>();
        schemaItems.forEach(item => {
            if (item.valueTypeId === StructureItemType.NumberArray) {
                for (let i = 0; i < item.arrayLength!; i++) {
                    const headerName = `${item.name}_${i}`;
                    expectedHeaders.add(headerName);
                    if (item.typeId === StructureColumnTypeEnum.Info) {
                        optionalHeaders.add(headerName);
                    }
                }
            } else {
                expectedHeaders.add(item.name);
                if (item.typeId === StructureColumnTypeEnum.Info) {
                    optionalHeaders.add(item.name);
                }
            }
        });

        const missingHeaders: string[] = [];
        expectedHeaders.forEach(expected => {
            if (!header.includes(expected) && !optionalHeaders.has(expected)) {
                missingHeaders.push(expected);
            }
        });

        if (missingHeaders.length > 0) {
            return {
                data: [],
                schemaOrdering: [],
                isValid: false,
                error: `Missing required columns: ${missingHeaders.join(", ")}`
            };
        }

        const unexpectedHeaders = header.filter(h => !expectedHeaders.has(h));
        if (unexpectedHeaders.length > 0) {
            return {
                data: [],
                schemaOrdering: [],
                isValid: false,
                error: `Unexpected columns: ${unexpectedHeaders.join(", ")}`
            };
        }

        const schemaItemMap = new Map<string, SchemaItem>();
        schemaItems.forEach(item => {
            schemaItemMap.set(item.name, item);
        });

        const headerSet = new Set(header);

        const data: SchemaDataRow[] = [];

        for (let rowIndex = 0; rowIndex < dataRows.length; rowIndex++) {
            const cells = dataRows[rowIndex];
            const lineNumber = rowIndex + 2; // +1 for header, +1 for 0-based index

            // Validate that the row has the correct number of columns
            if (cells.length !== header.length) {
                throw new Error(`Line ${lineNumber}: Expected ${header.length} columns but found ${cells.length}. Column mismatch: header has [${header.join(", ")}]`);
            }

            const row: SchemaDataRow = [];

            let headerIndex = 0;
            while (headerIndex < header.length) {
                const headerCol = header[headerIndex];

                const arrayMatch = headerCol.match(/^(.+)_(\d+)$/);

                if (arrayMatch) {
                    const baseName = arrayMatch[1];
                    const schemaItem = schemaItemMap.get(baseName);

                    if (!schemaItem || schemaItem.valueTypeId !== StructureItemType.NumberArray) {
                        throw new Error(`Line ${lineNumber}: Unknown array field: ${headerCol}`);
                    }

                    const arrayValues: number[] = [];
                    for (let i = 0; i < schemaItem.arrayLength!; i++) {
                        const arrayColName = `${baseName}_${i}`;

                        if (schemaItem.typeId === StructureColumnTypeEnum.Info && !headerSet.has(arrayColName)) {
                            continue;
                        }

                        if (headerIndex >= header.length) {
                            throw new Error(`Line ${lineNumber}: Header index out of bounds for ${baseName}_${i}`);
                        }

                        if (headerIndex >= cells.length) {
                            throw new Error(`Line ${lineNumber}: Missing value for ${baseName}_${i}`);
                        }

                        const cellValue = cells[headerIndex].trim();
                        if (cellValue === undefined || cellValue === '') {
                            throw new Error(`Line ${lineNumber}: Empty or missing value for ${header[headerIndex]}`);
                        }

                        const value = parseFloat(cellValue);
                        if (isNaN(value)) {
                            throw new Error(`Line ${lineNumber}: Invalid number for ${header[headerIndex]} (value: "${cellValue}")`);
                        }
                        arrayValues.push(value);
                        headerIndex++;
                    }

                    if (arrayValues.length > 0 || schemaItem.typeId !== StructureColumnTypeEnum.Info) {
                        row.push(arrayValues);
                    }
                } else {
                    const schemaItem = schemaItemMap.get(headerCol);

                    if (!schemaItem) {
                        throw new Error(`Line ${lineNumber}: Unknown field: ${headerCol}`);
                    }

                    if (headerIndex >= cells.length) {
                        if (schemaItem.typeId === StructureColumnTypeEnum.Info) {
                            headerIndex++;
                            continue;
                        }
                        throw new Error(`Line ${lineNumber}: Missing value for ${headerCol}`);
                    }

                    const cellValue = cells[headerIndex];
                    if (cellValue === undefined) {
                        throw new Error(`Line ${lineNumber}: Undefined value for ${headerCol}`);
                    }

                    row.push(parseValue(cellValue, schemaItem, lineNumber));
                    headerIndex++;
                }
            }

            data.push(row);
        }

        return {
            data,
            schemaOrdering: Array.from(schemaOrderingSet),
            isValid: true
        };
    } catch (error) {
        return {
            data: [],
            schemaOrdering: [],
            isValid: false,
            error: (error as Error).message
        };
    }
}

export interface ParseDataOptions {
    fileType?: FileType;
}

export function parseFileData(content: string, schemaItems: SchemaItem[], options?: ParseDataOptions): ParseResult {
    const fileType = options?.fileType ?? detectFileType(content);

    if (fileType === FileType.Json) {
        return parseJsonData(content, schemaItems);
    }
    return parseCsvData(content, schemaItems);
}

export function parseJsonData(jsonString: string, schemaItems: SchemaItem[]): ParseResult {
    try {
        // Parse JSON
        let parsed: unknown;
        try {
            parsed = JSON.parse(jsonString);
        } catch {
            return {
                data: [],
                schemaOrdering: [],
                isValid: false,
                error: "Invalid JSON format"
            };
        }

        // Validate it's an array
        if (!Array.isArray(parsed)) {
            return {
                data: [],
                schemaOrdering: [],
                isValid: false,
                error: "JSON must be an array of objects"
            };
        }

        if (parsed.length === 0) {
            return {
                data: [],
                schemaOrdering: [],
                isValid: false,
                error: "JSON array must have at least one item"
            };
        }

        // Validate all items are objects
        for (let i = 0; i < parsed.length; i++) {
            const item = parsed[i];
            if (item === null || typeof item !== 'object' || Array.isArray(item)) {
                return {
                    data: [],
                    schemaOrdering: [],
                    isValid: false,
                    error: `Item at index ${i} is not an object`
                };
            }
        }

        const objects = parsed as Record<string, unknown>[];

        // Build expected keys from schema
        const expectedKeys = new Set<string>();
        const optionalKeys = new Set<string>();
        schemaItems.forEach(item => {
            expectedKeys.add(item.name);
            if (item.typeId === StructureColumnTypeEnum.Info) {
                optionalKeys.add(item.name);
            }
        });

        // Validate keys in first object
        const firstKeys = Object.keys(objects[0]);

        const missingKeys = Array.from(expectedKeys).filter(k => !firstKeys.includes(k) && !optionalKeys.has(k));
        if (missingKeys.length > 0) {
            return {
                data: [],
                schemaOrdering: [],
                isValid: false,
                error: `Missing required fields: ${missingKeys.join(", ")}`
            };
        }

        const unexpectedKeys = firstKeys.filter(k => !expectedKeys.has(k));
        if (unexpectedKeys.length > 0) {
            return {
                data: [],
                schemaOrdering: [],
                isValid: false,
                error: `Unexpected fields: ${unexpectedKeys.join(", ")}`
            };
        }

        // Validate all objects have the same keys
        for (let i = 1; i < objects.length; i++) {
            const currentKeys = Object.keys(objects[i]);
            const objMissingKeys = firstKeys.filter(k => !currentKeys.includes(k));
            const objExtraKeys = currentKeys.filter(k => !firstKeys.includes(k));

            if (objMissingKeys.length > 0) {
                return {
                    data: [],
                    schemaOrdering: [],
                    isValid: false,
                    error: `Item at index ${i} is missing keys: ${objMissingKeys.join(", ")}`
                };
            }
            if (objExtraKeys.length > 0) {
                return {
                    data: [],
                    schemaOrdering: [],
                    isValid: false,
                    error: `Item at index ${i} has unexpected keys: ${objExtraKeys.join(", ")}`
                };
            }
        }

        // Determine schema ordering from the JSON keys (preserving order from first object)
        const schemaOrdering = firstKeys.filter(k => expectedKeys.has(k));

        const data: SchemaDataRow[] = [];

        for (let rowIndex = 0; rowIndex < objects.length; rowIndex++) {
            const obj = objects[rowIndex];
            const row: SchemaDataRow = [];

            for (const schemaItem of schemaItems) {
                const value = obj[schemaItem.name];

                // Check for null/undefined
                if (value === null) {
                    throw new Error(`Row ${rowIndex + 1}, field "${schemaItem.name}": value is null`);
                }
                if (value === undefined) {
                    if (schemaItem.typeId === StructureColumnTypeEnum.Info) {
                        continue;
                    }
                    throw new Error(`Row ${rowIndex + 1}, field "${schemaItem.name}": value is undefined`);
                }

                // Parse value based on schema type
                switch (schemaItem.valueTypeId) {
                    case StructureItemType.NumberArray:
                        if (!Array.isArray(value)) {
                            throw new Error(`Row ${rowIndex + 1}, field "${schemaItem.name}": expected array, got ${typeof value}`);
                        }
                        if (value.length !== schemaItem.arrayLength) {
                            throw new Error(`Row ${rowIndex + 1}, field "${schemaItem.name}": expected array length ${schemaItem.arrayLength}, got ${value.length}`);
                        }
                        const numberArray: number[] = [];
                        for (let i = 0; i < value.length; i++) {
                            const num = value[i];
                            if (typeof num !== 'number' || isNaN(num)) {
                                throw new Error(`Row ${rowIndex + 1}, field "${schemaItem.name}[${i}]": expected number, got "${num}"`);
                            }
                            numberArray.push(num);
                        }
                        row.push(numberArray);
                        break;

                    case StructureItemType.Number:
                        if (typeof value !== 'number' || isNaN(value)) {
                            throw new Error(`Row ${rowIndex + 1}, field "${schemaItem.name}": expected number, got "${value}"`);
                        }
                        row.push(value);
                        break;

                    case StructureItemType.Category:
                        const strValue = String(value);
                        if (!schemaItem.categories || schemaItem.categories.length === 0) {
                            throw new Error(`Row ${rowIndex + 1}, field "${schemaItem.name}": no categories defined`);
                        }
                        const validCategories = schemaItem.categories.map(c => c.value.toLowerCase());
                        if (!validCategories.includes(strValue.toLowerCase())) {
                            throw new Error(`Row ${rowIndex + 1}, field "${schemaItem.name}": invalid category "${strValue}". Valid values: ${validCategories.join(", ")}`);
                        }
                        row.push(strValue);
                        break;

                    case StructureItemType.String:
                        row.push(String(value));
                        break;

                    default:
                        row.push(String(value));
                }
            }

            data.push(row);
        }

        return {
            data,
            schemaOrdering,
            isValid: true
        };
    } catch (error) {
        return {
            data: [],
            schemaOrdering: [],
            isValid: false,
            error: (error as Error).message
        };
    }
}

export function dataToJson(data: SchemaDataRow[], prettyPrint: boolean = true): string {
    return JSON.stringify(data, null, prettyPrint ? 2 : 0);
}

// Configuration for category detection
const CATEGORY_THRESHOLD = 0.5; // If unique values are less than 50% of total rows, consider it a category
const MAX_CATEGORY_VALUES = 100; // Maximum unique values to consider as category

export interface ParseSchemaResult {
    schemaItems: SchemaItem[];
    isValid: boolean;
    error?: string;
}

export interface ParseSchemaOptions {
    fileType?: FileType;
}

export function parseFileSchema(content: string, options?: ParseSchemaOptions): ParseSchemaResult {
    const fileType = options?.fileType ?? detectFileType(content);

    if (fileType === FileType.Json) {
        return parseJsonSchema(content);
    }
    return parseCsvSchema(content);
}

export function parseJsonSchema(jsonString: string): ParseSchemaResult {
    try {
        // Parse JSON
        let parsed: unknown;
        try {
            parsed = JSON.parse(jsonString);
        } catch {
            return {
                schemaItems: [],
                isValid: false,
                error: "Invalid JSON format"
            };
        }

        if (!Array.isArray(parsed)) {
            return {
                schemaItems: [],
                isValid: false,
                error: "JSON must be an array of objects"
            };
        }

        if (parsed.length === 0) {
            return {
                schemaItems: [],
                isValid: false,
                error: "JSON array must have at least one item"
            };
        }

        for (let i = 0; i < parsed.length; i++) {
            const item = parsed[i];
            if (item === null || typeof item !== 'object' || Array.isArray(item)) {
                return {
                    schemaItems: [],
                    isValid: false,
                    error: `Item at index ${i} is not an object`
                };
            }
        }

        const objects = parsed as Record<string, unknown>[];

        const firstKeys = Object.keys(objects[0]);
        if (firstKeys.length === 0) {
            return {
                schemaItems: [],
                isValid: false,
                error: "Objects must have at least one property"
            };
        }

        for (let i = 1; i < objects.length; i++) {
            const currentKeys = Object.keys(objects[i]);
            const missingKeys = firstKeys.filter(k => !currentKeys.includes(k));
            const extraKeys = currentKeys.filter(k => !firstKeys.includes(k));

            if (missingKeys.length > 0) {
                return {
                    schemaItems: [],
                    isValid: false,
                    error: `Item at index ${i} is missing keys: ${missingKeys.join(", ")}`
                };
            }
            if (extraKeys.length > 0) {
                return {
                    schemaItems: [],
                    isValid: false,
                    error: `Item at index ${i} has unexpected keys: ${extraKeys.join(", ")}`
                };
            }
        }

        const schemaItems: SchemaItem[] = [];

        for (const key of firstKeys) {
            const values = objects.map(obj => obj[key]);
            const schemaItem = inferJsonFieldType(key, values);
            schemaItems.push(schemaItem);
        }

        return {
            schemaItems,
            isValid: true
        };
    } catch (error) {
        return {
            schemaItems: [],
            isValid: false,
            error: (error as Error).message
        };
    }
}

function inferJsonFieldType(name: string, values: unknown[]): SchemaItem {
    // Check for null/undefined values - these are not allowed
    for (let i = 0; i < values.length; i++) {
        if (values[i] === null) {
            throw new Error(`Field "${name}" has null value at index ${i}`);
        }
        if (values[i] === undefined) {
            throw new Error(`Field "${name}" has undefined value at index ${i}`);
        }
    }

    if (values.length === 0) {
        throw new Error(`Field "${name}" has no values to infer type from`);
    }

    // Check if all values are arrays of numbers (NumberArray)
    const allArraysOfNumbers = values.every(v =>
        Array.isArray(v) && v.length > 0 && v.every(item => typeof item === 'number' && !isNaN(item))
    );

    if (allArraysOfNumbers) {
        const arrays = values as number[][];
        const firstLength = arrays[0].length;

        // Validate all arrays have the same length
        for (let i = 1; i < arrays.length; i++) {
            if (arrays[i].length !== firstLength) {
                throw new Error(`Field "${name}" has inconsistent array lengths: expected ${firstLength}, found ${arrays[i].length} at index ${i}`);
            }
        }

        return {
            name,
            valueTypeId: StructureItemType.NumberArray,
            typeId: StructureColumnTypeEnum.Feature,
            arrayLength: firstLength
        };
    }

    // Check if all values are numbers
    const allNumbers = values.every(v => typeof v === 'number' && !isNaN(v));

    if (allNumbers) {
        return {
            name,
            valueTypeId: StructureItemType.Number,
            typeId: StructureColumnTypeEnum.Feature
        };
    }

    // Convert to strings for category/string detection
    const stringValues = values.map(v => String(v));

    // Check if it's a category (limited unique values that repeat)
    const uniqueValues = new Set(stringValues.map(v => v.toLowerCase()));
    const uniqueRatio = uniqueValues.size / stringValues.length;

    if (uniqueValues.size <= MAX_CATEGORY_VALUES && uniqueRatio <= CATEGORY_THRESHOLD) {
        const categories: SchemaItemCategory[] = Array.from(uniqueValues).map((value, index) => ({
            value: value.toLowerCase(),
            index
        }));

        return {
            name,
            valueTypeId: StructureItemType.Category,
            typeId: StructureColumnTypeEnum.Feature,
            categories
        };
    }

    // Default to string
    return {
        name,
        valueTypeId: StructureItemType.String,
        typeId: StructureColumnTypeEnum.Feature
    };
}

export function parseCsvSchema(csvString: string): ParseSchemaResult {
    try {
        const parseResult = Papa.parse<string[]>(csvString, {
            skipEmptyLines: true,
        });

        if (parseResult.errors && parseResult.errors.length > 0) {
            const firstError = parseResult.errors[0];
            return {
                schemaItems: [],
                isValid: false,
                error: `CSV parse error at row ${firstError.row}: ${firstError.message}`
            };
        }

        const rows = parseResult.data;

        if (!rows || rows.length < 2) {
            return {
                schemaItems: [],
                isValid: false,
                error: "CSV must have at least a header row and one data row"
            };
        }

        const header = rows[0].map((h: string) => h.trim());
        const dataRows = rows.slice(1);

        // Group headers by base name to detect arrays (e.g., column_0, column_1, column_2)
        const headerGroups = new Map<string, { indices: number[]; isArray: boolean }>();

        header.forEach((h, index) => {
            const arrayMatch = h.match(/^(.+)_(\d+)$/);
            if (arrayMatch) {
                const baseName = arrayMatch[1];

                if (!headerGroups.has(baseName)) {
                    headerGroups.set(baseName, { indices: [], isArray: true });
                }
                const group = headerGroups.get(baseName)!;
                group.indices.push(index);
            } else {
                if (!headerGroups.has(h)) {
                    headerGroups.set(h, { indices: [index], isArray: false });
                }
            }
        });

        const schemaItems: SchemaItem[] = [];

        for (const [name, group] of headerGroups) {
            if (group.isArray) {
                // Check if all array values are numbers
                let allNumbers = true;

                for (let rowIndex = 0; rowIndex < dataRows.length; rowIndex++) {
                    const row = dataRows[rowIndex];
                    for (const colIndex of group.indices) {
                        const value = row[colIndex];
                        if (value === undefined || value === null) {
                            throw new Error(`Row ${rowIndex + 2}, column "${header[colIndex]}": value is undefined or null`);
                        }
                        const trimmedValue = value.trim();
                        if (trimmedValue === '') {
                            throw new Error(`Row ${rowIndex + 2}, column "${header[colIndex]}": value is empty`);
                        }
                        if (isNaN(parseFloat(trimmedValue))) {
                            allNumbers = false;
                        }
                    }
                }

                if (allNumbers) {
                    schemaItems.push({
                        name,
                        valueTypeId: StructureItemType.NumberArray,
                        typeId: StructureColumnTypeEnum.Feature,
                        arrayLength: group.indices.length
                    });
                } else {
                    // Treat each column separately and infer type individually
                    for (const colIndex of group.indices) {
                        const columnValues: string[] = [];
                        for (const row of dataRows) {
                            columnValues.push(row[colIndex].trim());
                        }
                        const schemaItem = inferColumnType(header[colIndex], columnValues);
                        schemaItems.push(schemaItem);
                    }
                }
            } else {
                const colIndex = group.indices[0];
                const columnValues: string[] = [];

                for (let rowIndex = 0; rowIndex < dataRows.length; rowIndex++) {
                    const row = dataRows[rowIndex];
                    const value = row[colIndex];
                    if (value === undefined || value === null) {
                        throw new Error(`Row ${rowIndex + 2}, column "${name}": value is undefined or null`);
                    }
                    const trimmedValue = value.trim();
                    if (trimmedValue === '') {
                        throw new Error(`Row ${rowIndex + 2}, column "${name}": value is empty`);
                    }
                    columnValues.push(trimmedValue);
                }

                const schemaItem = inferColumnType(name, columnValues);
                schemaItems.push(schemaItem);
            }
        }

        return {
            schemaItems,
            isValid: true
        };
    } catch (error) {
        return {
            schemaItems: [],
            isValid: false,
            error: (error as Error).message
        };
    }
}

function inferColumnType(name: string, values: string[]): SchemaItem {
    if (values.length === 0) {
        throw new Error(`Column "${name}" has no values to infer type from`);
    }

    if (values.every(v => !isNaN(parseFloat(v)) && isFinite(parseFloat(v)))) {
        return {
            name,
            valueTypeId: StructureItemType.Number,
            typeId: StructureColumnTypeEnum.Feature
        };
    }

    // Check if it's a category (limited unique values that repeat)
    const uniqueValues = new Set(values.map(v => v.toLowerCase()));
    const uniqueRatio = uniqueValues.size / values.length;

    if (uniqueValues.size <= MAX_CATEGORY_VALUES && uniqueRatio <= CATEGORY_THRESHOLD) {
        const categories: SchemaItemCategory[] = Array.from(uniqueValues).map((value, index) => ({
            value: value.toLowerCase(),
            index
        }));

        return {
            name,
            valueTypeId: StructureItemType.Category,
            typeId: StructureColumnTypeEnum.Feature,
            categories
        };
    }

    // Default to string
    return {
        name,
        valueTypeId: StructureItemType.String,
        typeId: StructureColumnTypeEnum.Feature
    };
}

export function dataToCsv(data: SchemaDataRow[], schemaItems: SchemaItem[]): string {
    if (!data || data.length === 0) {
        return "";
    }

    // Build CSV header based on schema items
    const headers: string[] = [];
    schemaItems.forEach(item => {
        if (item.valueTypeId === StructureItemType.NumberArray) {
            for (let i = 0; i < item.arrayLength!; i++) {
                headers.push(`${item.name}_${i}`);
            }
        } else {
            headers.push(item.name);
        }
    });

    // Convert data rows to string arrays for PapaParse
    const csvRows: string[][] = [];

    for (const row of data) {
        const csvCells: string[] = [];

        for (let i = 0; i < row.length; i++) {
            const value = row[i];

            if (Array.isArray(value)) {
                value.forEach(v => {
                    csvCells.push(String(v));
                });
            } else {
                csvCells.push(String(value));
            }
        }

        csvRows.push(csvCells);
    }

    // Use PapaParse to generate RFC 4180 compliant CSV
    return Papa.unparse({
        fields: headers,
        data: csvRows
    });
}
